import { HttpClient, HttpContext, HttpContextToken, HttpRequest } from '@angular/common/http'
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core'
import { lastValueFrom, timeout } from 'rxjs'
import { HttpFailureKind, classifyHttpFailure, isRecoverableFailure, provesReachability } from '../util/http-failure.util'

/**
 * Abstaende zwischen zwei Selbstheilungs-Versuchen, degressiv. Vorn dicht, damit ein kurzer
 * Aussetzer sofort wieder eingefangen wird; hinten bei 30 s, damit ein laengerer Ausfall nicht
 * zum Dauerfeuer gegen ein ohnehin totes Backend wird. Der letzte Wert wiederholt sich.
 *
 * Die Leiter ist bewusst laenger als ein Deploy dauert: ein App Service braucht nach dem
 * Neustart 2–3,5 Minuten, bis er antwortet (SaniUp-Backend/CLAUDE.md). Genau diese Zeit soll
 * niemand vor einer Fehlermeldung sitzen und F5 druecken.
 */
export const CONNECTION_RETRY_DELAYS_MS = [3_000, 6_000, 12_000, 20_000, 30_000] as const

/** Nach dieser Zeit gilt ein Erreichbarkeits-Versuch als verloren und die Leiter macht weiter. */
const PROBE_TIMEOUT_MS = 10_000

/**
 * Markiert den Erreichbarkeits-Versuch des `ConnectionService`. Er soll die Verbindung belegen,
 * aber waehrend eines Ausfalls nicht im 30-Sekunden-Takt die Fehlerzentrale zumuellen.
 */
export const CONNECTION_PROBE = new HttpContextToken<boolean>(() => false)

export type ConnectionState = 'online' | 'unreachable' | 'offline'

/**
 * Beobachtet, ob SaniUp antwortet, und heilt sich selbst.
 *
 * Beobachtungspunkt fuer den AUSFALL ist der `errorInterceptorFn` — er sieht ohnehin jede Antwort,
 * es braucht also keinen eigenen Health-Endpunkt.
 *
 * Fuer die RUECKKEHR reicht das aber nicht, und daran ist die erste Fassung gescheitert: sie hat
 * bei jedem Versuch nur `retryTick` hochgezaehlt und darauf gehofft, dass irgendeine Ansicht eine
 * fehlgeschlagene Abfrage haelt und sie neu laedt (`trackResourceFailure`). Auf einer Seite, deren
 * Daten laengst geladen waren — der Normalfall, wenn das Netz waehrend der Arbeit wegbricht — ging
 * damit bei JEDEM Versuch null Anfrage raus. Der Countdown lief, im Netzwerk-Tab passierte nichts,
 * und der Streifen blieb stehen, bis jemand neu lud (gemeldet 2026-08-13: Laptop ueber Nacht zu,
 * am Morgen bestes Netz und trotzdem „Keine Internetverbindung"; ebenso nach einer Bahnfahrt).
 *
 * Deshalb fragt der Service selbst nach. Die zweite Fassung merkte sich dafuer die zuletzt
 * GESCHEITERTE GET-Abfrage — und hatte damit **dasselbe Loch eine Ebene tiefer** (gemeldet
 * 2026-08-14, Screenshot aus dem HiMi-Anlage-Assistenten): wer in einem Formular arbeitet, laedt
 * nichts mehr, er speichert. Es scheitert also ein POST/PUT, keine einzige GET-Abfrage, und der
 * Versuch hatte wieder nichts, was er haette abspielen koennen. Symptom bis aufs Haar dasselbe —
 * Countdown laeuft, Netzwerk-Tab leer, Streifen steht bis F5.
 *
 * Gemerkt wird deshalb JEDE GET-Abfrage der App (`noteRequest`), nicht erst eine gescheiterte:
 * auf einer Seite, die einmal geladen hat, gibt es immer eine — und genau die ist der Beleg.
 */
@Injectable()
export class ConnectionService {
  private readonly httpClient = inject(HttpClient)

  private readonly state = signal<ConnectionState>('online')

  /** Sichtbar fuer Banner und Fehlerzustaende: SaniUp antwortet gerade nicht. */
  readonly connectionState = this.state.asReadonly()
  readonly isDegraded = computed(() => this.state() !== 'online')

  /**
   * Zaehlt jeden Versuch hoch. Wer wegen eines heilbaren Fehlers nichts anzeigen kann, haengt
   * sich daran und laedt neu — der Service selbst kennt die betroffenen Abfragen nicht.
   */
  readonly retryTick = signal(0)

  /** Sekunden bis zum naechsten Versuch; `null`, wenn gerade keiner ansteht. */
  readonly secondsUntilRetry = signal<number | null>(null)

  private attempt = 0
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private countdownTimer: ReturnType<typeof setInterval> | null = null

  /**
   * Die zuletzt abgesetzte GET-Abfrage — der Beleg, mit dem sich die Rueckkehr pruefen laesst.
   * Dieselbe Route, dieselbe Authentifizierung, dieselbe CORS-Freigabe wie im echten Betrieb; ein
   * separater Health-Ping traefe eine andere Strecke und waere damit die unzuverlaessigere Aussage.
   *
   * Gemerkt wird JEDE GET-Abfrage, nicht nur eine gescheiterte — daran ist die zweite Fassung
   * gescheitert (siehe Klassenkommentar). Nur GET, weil nur GET gefahrlos wiederholbar ist; ein
   * fehlgeschlagenes Speichern darf der Service NIE von sich aus nachschiessen.
   */
  private lastGetUrl: string | null = null
  private probeRunning = false

  constructor() {
    // Das Netz ist zurueck: nicht auf den Countdown warten, das ist der eine Moment, in dem ein
    // Versuch garantiert mehr Sinn hat als vorher. Nur wenn ueberhaupt etwas zu heilen ist —
    // sonst wirft ein Netzwechsel im laufenden Betrieb eine Leiter an, die niemand braucht.
    const onOnline = () => {
      if (this.state() !== 'online') {
        this.retryNow()
      }
    }
    const onOffline = () => {
      this.state.set('offline')
      // Auch dieser Weg braucht die Leiter. Ohne sie stuende der Streifen ohne Countdown da und
      // haenge allein am `online`-Ereignis — und genau das bleibt nach einem Ruhezustand des
      // Rechners gern aus.
      if (this.retryTimer === null) {
        this.scheduleRetry()
      }
    }
    // Im Hintergrund-Tab laufen zu lassen bringt nichts und kostet Anfragen; beim Zurueckkommen
    // ist dafuer sofort ein Versuch faellig, damit die Seite nicht veraltet dasteht.
    const onVisibility = () => {
      if (document.hidden) {
        this.clearTimers()
        this.secondsUntilRetry.set(null)
      } else if (this.state() !== 'online') {
        this.retryNow()
      }
    }

    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    document.addEventListener('visibilitychange', onVisibility)

    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      document.removeEventListener('visibilitychange', onVisibility)
      this.clearTimers()
    })
  }

  /** Eine Antwort ist angekommen — damit ist die Verbindung belegt, egal welche Route es war. */
  reportSuccess(): void {
    if (this.state() === 'online') {
      return
    }
    this.state.set('online')
    this.attempt = 0
    this.clearTimers()
    this.secondsUntilRetry.set(null)
  }

  /**
   * Meldet dem Service JEDE ausgehende Abfrage — der Interceptor ruft das auf dem Hinweg auf,
   * also unabhaengig davon, wie sie ausgeht.
   *
   * Genau das ist der Punkt: haengen bleibt der Streifen nicht beim Laden, sondern beim
   * Speichern in einem Formular. Wuerde der Service nur gescheiterte Abfragen mitschreiben,
   * haette er dort ausschliesslich POST/PUT gesehen — und nichts, was er wiederholen darf.
   */
  noteRequest(request: HttpRequest<unknown>): void {
    if (request.method !== 'GET' || request.context.get(CONNECTION_PROBE)) {
      return
    }

    // Downloads bleiben draussen: ein PDF oder ein Export bei jedem Versuch erneut zu ziehen waere
    // ein teurer Beleg fuer eine Frage, die jede kleine Abfrage genauso beantwortet.
    if (request.responseType !== 'json' && request.responseType !== 'text') {
      return
    }

    this.lastGetUrl = request.urlWithParams
  }

  /** Eine Abfrage ist fehlgeschlagen. Nur heilbare Fehler starten die Leiter. */
  reportFailure(error: unknown): void {
    // Eine Fehlerantwort ist immer noch eine Antwort. Kommt der wiederholte Versuch als 401 oder
    // 500 zurueck, ist SaniUp erreichbar — dann gehoert der Streifen weg, auch wenn die Abfrage
    // selbst nichts geliefert hat.
    if (provesReachability(error)) {
      this.reportSuccess()
      return
    }

    const kind = classifyHttpFailure(error)
    if (!isRecoverableFailure(kind)) {
      return
    }

    this.state.set(kind === 'offline' ? 'offline' : 'unreachable')

    // Die Leiter haelt sich selbst am Laufen (siehe `scheduleRetry`). Ein zweiter Fehler waehrend
    // eines laufenden Countdowns darf sie deshalb nicht ein zweites Mal anwerfen — sonst wird aus
    // zehn fehlgeschlagenen Abfragen einer Seite ein zehnfach schnelleres Nachschiessen.
    if (this.retryTimer === null) {
      this.scheduleRetry()
    }
  }

  /** Von Hand ausgeloest („Jetzt versuchen"). Setzt die Leiter zurueck. */
  retryNow(): void {
    this.clearTimers()
    this.attempt = 0
    this.fireRetry()
  }

  private scheduleRetry(): void {
    // Die Leiter laeuft ausschliesslich, solange etwas zu heilen ist. Ohne diese Zusicherung
    // genuegt ein einziger Aufruf zur falschen Zeit, damit sie bis zum naechsten Neuladen der
    // Seite im 30-Sekunden-Takt weiterlaeuft — unsichtbar, weil der Streifen dann nichts anzeigt.
    if (this.state() === 'online') {
      return
    }

    const delay = CONNECTION_RETRY_DELAYS_MS[Math.min(this.attempt, CONNECTION_RETRY_DELAYS_MS.length - 1)]
    this.attempt++

    this.secondsUntilRetry.set(Math.round(delay / 1000))
    this.countdownTimer = setInterval(() => {
      this.secondsUntilRetry.update((seconds) => (seconds === null ? null : Math.max(0, seconds - 1)))
    }, 1000)

    this.retryTimer = setTimeout(() => this.fireRetry(), delay)
  }

  private fireRetry(): void {
    this.clearTimers()
    this.secondsUntilRetry.set(null)
    this.retryTick.update((tick) => tick + 1)

    // Weiterlaufen, bis eine Antwort ankommt: `reportSuccess` raeumt die Leiter ab. Ohne das
    // haette ein Versuch, der ins Leere laeuft (z.B. weil gerade keine Abfrage offen ist), die
    // Selbstheilung still beendet.
    this.scheduleRetry()

    // NACH `scheduleRetry`, nicht davor: der Versuch kann synchron mit `reportSuccess` enden, und
    // dessen `clearTimers` muss den gerade gestellten Wecker treffen — sonst tickt die Leiter
    // weiter, obwohl die Verbindung schon wieder steht.
    void this.probe()
  }

  /**
   * Belegt aktiv, ob wieder etwas ankommt.
   *
   * Der `retryTick` allein stupst nur Ansichten an, die gerade eine fehlgeschlagene Abfrage
   * halten — auf jeder anderen Seite ging damit gar nichts raus (siehe Klassenkommentar).
   */
  private async probe(): Promise<void> {
    if (this.probeRunning) {
      return
    }

    const url = this.lastGetUrl
    if (url === null) {
      // Es gibt nichts, womit sich Erreichbarkeit belegen liesse — nach `noteRequest` heisst das:
      // die App hat seit dem Start nicht eine einzige Abfrage geladen. Was sich dann noch belegen
      // laesst: dass die Behauptung „Keine Internetverbindung" nicht mehr stimmt. Sie stammt aus
      // `navigator.onLine`, also entscheidet dieselbe Quelle auch ueber ihr Ende — alles andere
      // waere ein Zustand, aus dem die Seite ohne F5 nie wieder herausfindet.
      if (this.state() === 'offline' && navigator.onLine) {
        this.reportSuccess()
      }
      return
    }

    this.probeRunning = true
    try {
      // Ausgewertet wird die Antwort nicht hier, sondern im `errorInterceptorFn`: der meldet
      // Erfolg wie Fehlschlag ohnehin an diesen Service zurueck. `text` statt json, damit eine
      // unerwartete Nutzlast den Versuch nicht am Parsen scheitern laesst.
      await lastValueFrom(
        this.httpClient
          .get(url, {
            context: new HttpContext().set(CONNECTION_PROBE, true),
            observe: 'response',
            responseType: 'text',
          })
          .pipe(timeout(PROBE_TIMEOUT_MS)),
      )
    } catch {
      // Kein eigenes Zutun noetig — der Interceptor hat den Ausgang bereits gemeldet.
    } finally {
      this.probeRunning = false
    }
  }

  private clearTimers(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
    }
  }
}

/** Nur fuer Anzeigen, die den Grund benennen wollen, ohne selbst einen Fehler zu haben. */
export function connectionStateKind(state: ConnectionState): HttpFailureKind {
  return state === 'offline' ? 'offline' : 'unreachable'
}
